import { markTaskAsDone } from "interfaces/task";

interface Task {
    _id: string;
    content: string;
    dueDate: string;
    status: string;
    customer: {
        _id: string;
        company: string;
        assignedTo?: Array<{ _id: string; fullName: string }>;
    };
}

interface TaskAgendaProps {
    tasks: Task[];
    onTaskDone: (taskId: string) => void;
}

const isToday = (date: Date) => {
    const today = new Date();
    return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    );
};

const isTomorrow = (date: Date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return (
        date.getDate() === tomorrow.getDate() &&
        date.getMonth() === tomorrow.getMonth() &&
        date.getFullYear() === tomorrow.getFullYear()
    );
};

const formatDayLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return "Aujourd'hui";
    if (isTomorrow(date)) return "Demain";
    return date.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
};

const getDayKey = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const isOverdue = (dateStr: string) => {
    const date = new Date(dateStr);
    date.setHours(23, 59, 59, 999);
    return date < new Date();
};

const TaskAgenda = ({ tasks, onTaskDone }: TaskAgendaProps) => {
    // Group tasks by day
    const grouped: Record<string, Task[]> = {};
    tasks.forEach((task) => {
        const key = getDayKey(task.dueDate);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(task);
    });

    const sortedDays = Object.keys(grouped).sort();

    if (sortedDays.length === 0) {
        return (
            <div className="text-center py-[70px]">
                <p className="text-2xl text-gray-600 font-bold">
                    Aucune tâche pour le moment.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {sortedDays.map((day) => {
                const dayTasks = grouped[day];
                const firstDate = new Date(dayTasks[0].dueDate);
                const overdue = isOverdue(dayTasks[0].dueDate) && !isToday(firstDate);

                return (
                    <div key={day}>
                        {/* Day header */}
                        <div className="flex items-center gap-3 mb-3">
                            <div
                                className={`flex flex-col items-center justify-center w-12 h-12 rounded-xl text-white font-bold shadow-md flex-shrink-0 ${
                                    isToday(firstDate)
                                        ? "bg-toola-500"
                                        : overdue
                                        ? "bg-red-400"
                                        : "bg-gray-400"
                                }`}
                            >
                                <span className="text-lg leading-none">
                                    {firstDate.getDate()}
                                </span>
                                <span className="text-[10px] uppercase leading-none">
                                    {firstDate.toLocaleDateString("fr-FR", { month: "short" })}
                                </span>
                            </div>
                            <div>
                                <p
                                    className={`font-semibold capitalize text-base ${
                                        isToday(firstDate)
                                            ? "text-toola-500"
                                            : overdue
                                            ? "text-red-500"
                                            : "text-navy-700"
                                    }`}
                                >
                                    {formatDayLabel(dayTasks[0].dueDate)}
                                    {overdue && (
                                        <span className="ml-2 text-xs font-normal text-red-400">
                                            (en retard)
                                        </span>
                                    )}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {dayTasks.length} tâche
                                    {dayTasks.length > 1 ? "s" : ""}
                                </p>
                            </div>
                        </div>

                        {/* Task cards */}
                        <div className="ml-6 border-l-2 border-gray-100 pl-6 space-y-2">
                            {dayTasks.map((task) => {
                                const commercials = task.customer?.assignedTo;
                                return (
                                    <div
                                        key={task._id}
                                        className={`relative flex items-start justify-between gap-4 rounded-xl p-4 ${
                                            overdue && !isToday(new Date(task.dueDate))
                                                ? "bg-red-50"
                                                : "bg-lightPrimary"
                                        }`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-navy-700 text-sm truncate">
                                                {task.customer?.company}
                                            </p>
                                            <p className="text-gray-500 text-sm mt-0.5">
                                                {task.content}
                                            </p>
                                            {commercials && commercials.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {commercials.map((c) => (
                                                        <span
                                                            key={c._id}
                                                            className="text-[11px] bg-blue-100 text-blue-700 rounded-full px-2 py-0.5"
                                                        >
                                                            {c.fullName}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            className="flex-shrink-0 bg-toola-400 text-white text-xs py-1.5 px-3 rounded-lg whitespace-nowrap"
                                            onClick={async () => {
                                                await markTaskAsDone(task._id);
                                                onTaskDone(task._id);
                                            }}
                                        >
                                            Tâche effectuée
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default TaskAgenda;
